import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  rucksack,
} from '@black-bear-energy/rucksack'
import { SwapVertRounded } from '@mui/icons-material'
import { MenuButton } from './MenuButton'
import { MenuButtonSection } from './MenuButtonSection'

function ignoreDeselect<T>(setValue: (value: T) => void) {
  return (_event: React.MouseEvent<HTMLElement>, value: T | null) => {
    if (value !== null) {
      setValue(value)
    }
  }
}

export interface SortButtonProps<T extends string> {
  sortBy: T
  displayNames: Record<T, string>
  setSortBy: (sortBy: T) => void
  sortAsc: boolean
  setSortAsc: (sortAsc: boolean) => void
  handleReset: () => void
  resetDisabled: boolean
  showSortByLabel: boolean
}

export const SortButton = <T extends string>({
  sortBy,
  displayNames,
  setSortBy,
  sortAsc,
  setSortAsc,
  handleReset,
  resetDisabled,
  showSortByLabel,
}: SortButtonProps<T>) => {
  const handleSortByChange = ignoreDeselect(setSortBy)
  const handleSortAscChange = ignoreDeselect(setSortAsc)

  const sortByValues = Object.keys(displayNames) as T[]

  return (
    <MenuButton
      label={
        showSortByLabel
          ? `sort by: ${displayNames[sortBy]} (${sortAsc ? 'asc' : 'desc'})`
          : undefined
      }
      icon={<SwapVertRounded />}
      align="right"
      reset={{ handleReset, resetDisabled }}
    >
      <Box component="li" sx={{ paddingY: 1 }}>
        <MenuButtonSection title="Sort By">
          <ToggleButtonGroup
            value={sortBy}
            exclusive
            onChange={handleSortByChange}
            size="small"
            sx={{
              py: '0.375rem',
              '& .MuiButtonBase-root': {
                color: rucksack.palette.text.primary,
              },
            }}
          >
            {sortByValues.map((sortByValue) => (
              <ToggleButton key={sortByValue} value={sortByValue}>
                {displayNames[sortByValue]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </MenuButtonSection>
        <MenuButtonSection title="Order">
          <ToggleButtonGroup
            value={sortAsc}
            exclusive
            onChange={handleSortAscChange}
            size="small"
            sx={{
              py: '0.375rem',
              '& .MuiButtonBase-root': {
                color: rucksack.palette.text.primary,
              },
            }}
          >
            <ToggleButton value={true}>Ascending</ToggleButton>
            <ToggleButton value={false}>Descending</ToggleButton>
          </ToggleButtonGroup>
        </MenuButtonSection>
      </Box>
    </MenuButton>
  )
}
